import React from 'react';
import './mainpage2.css';
import NavBar from './NavBar';
import whoWeAreImage from './assets/2.webp';
import customersImage from './assets/3.webp';

const capabilityCards = [
  {
    title: 'Modern Workflows',
    text: 'Orchestrate approvals, reminders, and handoffs across Microsoft 365 without adding another disconnected platform.',
  },
  {
    title: 'Trusted Automation',
    text: 'Build repeatable operations with clear ownership, audit visibility, and human checkpoints where needed.',
  },
  {
    title: 'Insight Everywhere',
    text: 'Bring data from documents, email, and records into one operational lens for better and faster decisions.',
  },
];

const productCards = [
  {
    title: 'PShareEmail',
    text: 'Efficiently save your emails to SharePoint, OneDrive, or Teams. Navigate documents, copy links, and easily locate emails or documents with our comprehensive Outlook add-in.',
    href: '/solutions/pshareemail',
  },
  {
    title: 'DocSearch Tool',
    text: 'Advanced search capabilities for Microsoft 365. Find documents, emails, and content across your entire organization quickly and efficiently.',
    href: '/solutions/docsearch',
  },
  {
    title: 'Search Assistant',
    text: 'Boost productivity with intelligent search for Microsoft 365 Teams and Outlook. Locate information faster and work smarter.',
    href: '/solutions/searchassistant',
  },
  {
    title: 'pEntities',
    text: 'Streamline entity management and metadata handling for Microsoft 365. Organize and track important business entities with intelligent tagging and classification.',
    href: '/solutions/pentities',
  },
];

const testimonials = [
  {
    quote:
      'Monitoring the progress of offers, agreements and customer follow up got as easy as it gets. With Meta-365 apps my team and the whole organization have got in-line.',
    author: 'Thanopoulos Dimitrios',
    role: 'Head & Co-Founder @4Fluids',
  },
  {
    quote:
      'Meta-365 apps save hours of time for me and my team spending on document management issues! In addition, they clear up to all of us the good practice in using meta data appropriately.',
    author: 'George Mazarakis',
    role: 'CEO and Co-Founder, PCC Hellas',
  },
  {
    quote:
      'PShareEmail and Search Assistant apps for Microsoft 365 Teams and Outlook have significantly boosted the productivity of our employees.',
    author: 'Stelios Georgiou',
    role: 'Information Technology Manager, Bioland Energy (Cyprus)',
  },
];

const deliverySteps = [
  {
    number: '01',
    title: 'Discover',
    text: 'Map your current process and identify where friction slows teams down.',
  },
  {
    number: '02',
    title: 'Design',
    text: 'Define a practical roadmap with governance, ownership, and measurable outcomes.',
  },
  {
    number: '03',
    title: 'Deliver',
    text: 'Launch in focused phases and keep iterating with real operational feedback.',
  },
];

function MainPage2() {
  return (
    <div className="mainpage2">
      <NavBar showHome={false} />

      <header className="mp2-hero">
        <div className="mp2-gradient-orb mp2-orb-a" aria-hidden="true" />
        <div className="mp2-gradient-orb mp2-orb-b" aria-hidden="true" />
        <div className="mp2-container mp2-hero-grid">
          <div className="mp2-hero-copy">
            <p className="mp2-eyebrow">Business transformation with clarity</p>
            <h1>Build a sharper digital backbone for your operations</h1>
            <p className="mp2-lead">
              This test layout explores a bolder homepage direction for Meta-365,
              inspired by modern consultancy websites and designed for stronger
              storytelling, credibility, and conversion.
            </p>
            <div className="mp2-hero-actions">
              <a className="mp2-btn mp2-btn-primary" href="/get-started">
                Start with a use case
              </a>
              <a className="mp2-btn mp2-btn-secondary" href="/contactus">
                Talk to the team
              </a>
            </div>
          </div>

          <div className="mp2-hero-panel" aria-label="Highlights">
            <p className="mp2-panel-title">Why teams switch to Meta-365</p>
            <ul>
              <li>Reduce repetitive administration and approval delays</li>
              <li>Keep compliance and operational logic in one place</li>
              <li>Scale process improvements across departments</li>
            </ul>
            <div className="mp2-kpi-row">
              <div>
                <span>35%</span>
                <p>average process time reduction</p>
              </div>
              <div>
                <span>4x</span>
                <p>faster onboarding of workflows</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main>
        <section className="mp2-section mp2-capabilities">
          <div className="mp2-container">
            <div className="mp2-section-head">
              <p className="mp2-eyebrow">Core capabilities</p>
              <h2>Focused outcomes, not feature overload</h2>
            </div>
            <article className="mp2-story-card">
              <div className="mp2-story-copy">
                <p className="mp2-eyebrow">Who we are</p>
                <h3>Our Mission</h3>
                <p>
                  Meta-365 is a forward-thinking technology company specializing in
                  the development of applications for Microsoft 365 and Azure.
                  Founded on the principles of innovation, quality, and customer
                  satisfaction, we strive to be at the forefront of the digital
                  transformation landscape.
                </p>
              </div>
              <div className="mp2-story-image-wrap">
                <img loading="lazy" decoding="async" src={whoWeAreImage} alt="Who We Are" className="mp2-story-image" />
              </div>
            </article>
            <div className="mp2-card-grid">
              {capabilityCards.map((card) => (
                <article className="mp2-card" key={card.title}>
                  <h3>{card.title}</h3>
                  <p>{card.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="mp2-section mp2-approach">
          <div className="mp2-container">
            <div className="mp2-section-head">
              <p className="mp2-eyebrow">Delivery approach</p>
              <h2>From complexity to momentum in three moves</h2>
            </div>
            <div className="mp2-timeline">
              {deliverySteps.map((step) => (
                <article className="mp2-step" key={step.number}>
                  <span className="mp2-step-number">{step.number}</span>
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="mp2-section mp2-products">
          <div className="mp2-container">
            <div className="mp2-section-head">
              <p className="mp2-eyebrow">Our products</p>
              <h2>Enterprise power, instantly available</h2>
            </div>
            <p className="mp2-subtext">
              Meta-365 runs your most critical journeys with precision and speed.
              Check out our leading solutions. Available through Microsoft
              Marketplace.
            </p>
            <div className="mp2-product-grid">
              {productCards.map((product) => (
                <article className="mp2-product-card" key={product.title}>
                  <h3>{product.title}</h3>
                  <p>{product.text}</p>
                  <a href={product.href}>Learn more -&gt;</a>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="mp2-section mp2-customers">
          <div className="mp2-container mp2-customers-grid">
            <div>
              <div className="mp2-section-head">
                <p className="mp2-eyebrow">What customers say</p>
                <h2>Proof from teams in production</h2>
              </div>
              <div className="mp2-testimonial-list">
                {testimonials.map((item) => (
                  <article className="mp2-testimonial" key={item.author}>
                    <p>"{item.quote}"</p>
                    <div>
                      <strong>{item.author}</strong>
                      <span>{item.role}</span>
                    </div>
                  </article>
                ))}
              </div>
            </div>
            <div className="mp2-customer-image-wrap">
              <img loading="lazy" decoding="async" src={customersImage} alt="Our Customers" className="mp2-customer-image" />
            </div>
          </div>
        </section>

        <section className="mp2-section mp2-cta">
          <div className="mp2-container mp2-cta-inner">
            <h2>Want to compare this layout against the current homepage?</h2>
            <p>
              Use this page as a visual experiment before deciding what to merge
              into production.
            </p>
            <a className="mp2-btn mp2-btn-primary" href="/mainpage2">
              Keep testing this version
            </a>
          </div>
        </section>
      </main>
    </div>
  );
}

export default MainPage2;
